import React, { useEffect, useRef, useState } from 'react';
import APIService from '../services/api';
import NuageReflexion from './NuageReflexion';

/**
 * Copilote sourcing — le troisième cerveau, embarqué dans la campagne.
 *
 * C'est l'agent ERPIA (POST /agent, même boucle, mêmes outils, même
 * confirmation), scoppé sur CETTE campagne : le front passe
 * `context.besoin_id`, et le serveur relit les faits de la campagne EN BASE
 * avant de répondre — jamais depuis le récit de l'écran. Il analyse ce que
 * chaque canal a donné, où sont les profils, ce qui bloque, et propose la
 * prochaine action. Une action d'écriture (lancer un canal, positionner)
 * n'est JAMAIS exécutée sans le clic de confirmation.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const mono = { fontFamily: "'Space Mono','SFMono-Regular',Consolas,monospace" };
const enTete = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}` });

// Des analyses en un clic — des questions, pas des réponses préécrites.
const RAPIDES = [
  ['Analyse la campagne', 'Analyse cette campagne : ce que chaque canal a donné, où sont les profils, ce qui bloque. Puis dis-moi LA prochaine action, chiffres à l’appui.'],
  ['Où élargir ?', 'Vu les résultats, faut-il élargir la zone, assouplir les critères, ou lancer un autre canal ? Réponds avec les chiffres et propose une action.'],
  ['Qui contacter en premier ?', 'Classe les profils retenus par priorité de contact avec une raison pour chacun, et précise lesquels ne peuvent pas recevoir d’InMail.'],
];
// Les sources, une par une : ce qui les limite ici, et les lancer d'ici. Le
// diagnostic est une lecture (mots et zone réellement cherchés, journal du
// moteur, santé) ; le lancement passe par la confirmation, comme le bouton.
const SOURCES_RAPIDES = [
  ['Améliorer GitHub', 'Qu’est-ce qui limite la recherche GitHub sur cette campagne, et comment l’améliorer ? Lis ce que la source a réellement cherché et ramené, puis donne-moi des actions concrètes : ce que je peux changer ici (zone, requête, seuil, relance), et ce qui se règle côté moteur.'],
  ['Améliorer la recherche web', 'Qu’est-ce qui limite la recherche web (profils publics) sur cette campagne, et comment l’améliorer ? Lis ce que la source a réellement cherché et ramené, puis donne-moi des actions concrètes : ce que je peux changer ici, et ce qui se règle côté moteur.'],
  ['Lancer une source', 'Quelle source non lancée vaut le coup sur cette campagne — GitHub, recherche web, Job-Room, vivier interne ? Dis pourquoi avec les chiffres, puis lance la plus utile : je confirmerai.'],
];

export default function CopiloteSourcing({ besoinId, showNotification, onRefresh }) {
  const [messages, setMessages] = useState([]);
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [outil, setOutil] = useState(null);
  // Le copilote travaille sans écrire : avant son premier mot, et quand le
  // flux se tait au milieu d'une réponse (il prépare l'outil suivant). Au-delà
  // de 900 ms de silence, le nuage revient.
  const [attente, setAttente] = useState(false);
  const silenceRef = useRef(null);
  const [aConfirmer, setAConfirmer] = useState(null);
  const finRef = useRef(null);
  const abortRef = useRef(null);
  useEffect(() => { finRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages, outil, attente, aConfirmer]);
  useEffect(() => () => { abortRef.current?.abort(); clearTimeout(silenceRef.current); }, []);

  const envoyer = async (texte) => {
    const t = String(texte || '').trim();
    if (!t || enCours || besoinId == null) return;
    const hist = [...messages, { role: 'user', content: t }];
    setMessages([...hist, { role: 'assistant', content: '' }]);
    setSaisie(''); setEnCours(true); setOutil(null); setAttente(true); setAConfirmer(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    const trame = (part) => {
      let ev = 'message', data = '';
      for (const l of part.split(/\r?\n/)) { if (l.startsWith('event:')) ev = l.slice(6).trim(); else if (l.startsWith('data:')) data += l.slice(5).trim(); }
      let j = {}; try { j = data ? JSON.parse(data) : {}; } catch { return; }
      if (ev === 'text_delta') {
        setOutil(null); setAttente(false);
        clearTimeout(silenceRef.current);
        silenceRef.current = setTimeout(() => setAttente(true), 900);
        setMessages(ms => { const c = ms.slice(); const d = c[c.length - 1]; if (d?.role === 'assistant') c[c.length - 1] = { ...d, content: (d.content || '') + (j.text || '') }; return c; });
      }
      else if (ev === 'tool_start' || ev === 'tool_progress') { clearTimeout(silenceRef.current); setOutil(j.label || j.name || null); }
      else if (ev === 'confirm_action') setAConfirmer({ name: j.name, confirmation_id: j.confirmation_id, summary: j.summary, detail: j.detail, input: j.input });
      else if (ev === 'error') showNotification?.(j.message || 'Le copilote a rencontré une erreur.', 'error');
    };
    try {
      const res = await fetch(`${BASE}/agent`, {
        method: 'POST', headers: { ...enTete(), Accept: 'text/event-stream' }, signal: ctrl.signal,
        body: JSON.stringify({ messages: hist.map(m => ({ role: m.role, content: m.content })).filter(m => m.content), context: { besoin_id: besoinId } }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
      for (;;) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split(/\r?\n\r?\n/); buf = parts.pop();
        parts.forEach(trame);
      }
      if (buf.trim()) trame(buf);
    } catch (e) { if (e.name !== 'AbortError') showNotification?.(`Copilote indisponible : ${e.message}`, 'error'); }
    finally { clearTimeout(silenceRef.current); setEnCours(false); setOutil(null); setAttente(false); }
  };

  // La confirmation passe par la MÊME route que le chat global : le serveur
  // exécute l'action proposée, pas le front.
  const confirmer = async (oui) => {
    const c = aConfirmer; if (!c) return;
    setAConfirmer(null);
    if (!oui) { setMessages(ms => [...ms, { role: 'assistant', content: 'Action annulée — rien n’a été fait.' }]); return; }
    try {
      const r = await fetch(`${BASE}/agent/confirm`, { method: 'POST', headers: enTete(), body: JSON.stringify({ name: c.name, input: c.input, confirmation_id: c.confirmation_id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false || j.error) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const txt = j.message || j.result?.message || j.data?.message || 'Action effectuée.';
      showNotification?.(txt, 'success');
      setMessages(ms => [...ms, { role: 'assistant', content: txt }]);
      onRefresh?.();
    } catch (e) { showNotification?.(`Action impossible : ${e.message}`, 'error'); }
  };

  return (
    <div className="erp-card cps" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: "'DM Sans','IBM Plex Sans',sans-serif" }}>
      <style>{`
        .cps-rapide { padding: 6px 11px; border-radius: 99px; border: 1px solid var(--erp-line); background: transparent; color: var(--erp-ink-strong); font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; }
        .cps-rapide:hover:not(:disabled) { border-color: var(--erp-accent); color: var(--erp-accent); }
        .cps-rapide:disabled { opacity: .5; cursor: default; }
        .cps-rapide:focus-visible { outline: 2px solid var(--erp-accent); outline-offset: 1px; }
        .cps-fil { max-height: 340px; overflow: auto; display: flex; flex-direction: column; gap: 8px; padding-right: 2px; }
        .cps-msg { max-width: 92%; padding: 8px 11px; border-radius: 10px; font-size: 12.5px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; }
        .cps-msg--user { align-self: flex-end; background: var(--erp-accent-soft); color: var(--erp-ink-strong); }
        .cps-msg--bot { align-self: flex-start; background: var(--erp-surface-muted); color: var(--erp-ink-strong); }
        .cps-saisie { flex: 1; min-width: 0; padding: 9px 12px; border-radius: 9px; border: 1px solid var(--erp-field-border, var(--erp-line)); background: var(--erp-surface); color: var(--erp-ink-strong); font-family: inherit; font-size: 13px; }
        .cps-saisie:focus { outline: 2px solid var(--erp-accent); outline-offset: 1px; }
        .cps-reflexion { display: inline-flex; align-items: center; gap: 8px; color: var(--erp-text-tertiary); }
        .cps-nuage { flex: none; color: var(--erp-ink-strong); }
        .cps-confirm { border: 1px solid var(--erp-accent); border-radius: 9px; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px; }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'var(--erp-surface-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--erp-accent)', flex: 'none' }}><BI icon="chat-square-text" style={{ fontSize: '14px' }} /></span>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div className="sva-kicker">Copilote sourcing</div>
          <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--erp-ink-strong)', letterSpacing: '-0.01em' }}>Le troisième cerveau lit cette campagne</div>
        </div>
        <span style={{ ...mono, fontSize: '9.5px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)' }}>faits relus en base à chaque question</span>
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {RAPIDES.map(([l, q]) => (
          <button key={l} type="button" className="cps-rapide" disabled={enCours || besoinId == null} onClick={() => envoyer(q)}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="sva-kicker" style={{ marginRight: '2px' }}>Sources</span>
        {SOURCES_RAPIDES.map(([l, q]) => (
          <button key={l} type="button" className="cps-rapide" disabled={enCours || besoinId == null} onClick={() => envoyer(q)}>{l}</button>
        ))}
      </div>

      {messages.length === 0 && !enCours && (
        <div style={{ fontSize: '12.5px', lineHeight: 1.55, color: 'var(--erp-text-secondary)' }}>
          Posez une question sur cette campagne, ou choisissez une analyse ci-dessus. Le copilote lit ce que chaque source a réellement cherché et ramené, dit ce qui la limite, et peut agir — lancer ou relancer une source, positionner un profil — toujours après votre confirmation.
        </div>
      )}

      {messages.length > 0 && (
        <div className="cps-fil" aria-live="polite">
          {messages.map((m, i) => (
            (m.content || (i === messages.length - 1 && enCours)) ? (
              <div key={i} className={`cps-msg ${m.role === 'user' ? 'cps-msg--user' : 'cps-msg--bot'}`}>
                {m.content || <span className="cps-reflexion" role="status"><NuageReflexion taille={28} className="cps-nuage" />{outil || 'Je lis la campagne…'}</span>}
              </div>
            ) : null
          ))}
          {enCours && (outil || attente) && messages[messages.length - 1]?.content && (
            <div className="cps-reflexion" role="status" style={{ ...mono, fontSize: '10.5px' }}><NuageReflexion taille={24} className="cps-nuage" />{outil || 'Réflexion…'}</div>
          )}
          {aConfirmer && (
            <div className="cps-confirm" role="dialog" aria-label="Confirmer l’action">
              <div className="sva-kicker">Action proposée — à confirmer</div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--erp-ink-strong)' }}>{aConfirmer.summary || aConfirmer.name}</div>
              {aConfirmer.detail && <div style={{ fontSize: '11.5px', color: 'var(--erp-text-secondary)' }}>{aConfirmer.detail}</div>}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" className="erp-btn erp-btn--primary" onClick={() => confirmer(true)}>Confirmer</button>
                <button type="button" className="erp-btn erp-btn--secondary" onClick={() => confirmer(false)}>Annuler</button>
              </div>
            </div>
          )}
          <div ref={finRef} />
        </div>
      )}

      <form style={{ display: 'flex', gap: '8px' }} onSubmit={(e) => { e.preventDefault(); envoyer(saisie); }}>
        <input className="cps-saisie" value={saisie} onChange={(e) => setSaisie(e.target.value)} disabled={enCours || besoinId == null}
          placeholder="Une question sur cette campagne…" aria-label="Question au copilote" />
        <button type="submit" className="erp-btn erp-btn--primary" disabled={enCours || !saisie.trim() || besoinId == null} aria-label="Envoyer">
          {/* Le travail se lit au nuage, dans le fil : le bouton ne tourne pas en plus. */}
          <BI icon="send" />
        </button>
      </form>
    </div>
  );
}
